import React from 'react';
import { attributeIconMap, type AttributeIconKey } from '../utils/attributeIcons';

export interface AttributeLabelProps {
  /** Semantischer Schlüssel aus der zentralen attributeIconMap. */
  iconKey: AttributeIconKey;
  /** Sichtbarer Labeltext (Kinder). */
  children: React.ReactNode;
  /**
   * Kompakte Variante für mobile Karten: kleineres Icon (h-3.5 w-3.5).
   * Standard ist h-4 w-4.
   */
  size?: 'sm' | 'md';
  /**
   * Farb-Akzent des Icons. Standard neutral-grau. „primary" nur für aktive
   * oder positiv hervorgehobene Zustände verwenden (Design-Regel 5).
   */
  tone?: 'neutral' | 'primary';
  /** Zusätzliche Klassen für den Wrapper. */
  className?: string;
  /** Zusätzliche Klassen für den Textteil. */
  textClassName?: string;
  /** Optionales htmlFor: rendert als <label> statt <span>. */
  htmlFor?: string;
}

const ICON_SIZE: Record<NonNullable<AttributeLabelProps['size']>, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
};

const ICON_TONE: Record<NonNullable<AttributeLabelProps['tone']>, string> = {
  neutral: 'text-gray-500 dark:text-gray-400',
  primary: 'text-primary-600 dark:text-primary-400',
};

/**
 * Einheitliches Feld-/Attributlabel mit vorangestelltem Icon.
 *
 * Nutzt die zentrale semantische Icon-Zuordnung (Issue #9). Icon und Text
 * werden vertikal zentriert mit `gap-1.5` ausgerichtet; das Icon ist
 * dekorativ (`aria-hidden`), die Information steckt im Text.
 *
 * @example
 * <AttributeLabel iconKey="email">E-Mail</AttributeLabel>
 * <AttributeLabel iconKey="email" htmlFor="email-input">E-Mail</AttributeLabel>
 */
const AttributeLabel: React.FC<AttributeLabelProps> = ({
  iconKey,
  children,
  size = 'md',
  tone = 'neutral',
  className = '',
  textClassName = '',
  htmlFor,
}) => {
  const Icon = attributeIconMap[iconKey]?.component;
  const content = (
    <>
      {Icon && (
        <Icon
          className={`${ICON_SIZE[size]} shrink-0 ${ICON_TONE[tone]}`}
          aria-hidden="true"
          focusable="false"
        />
      )}
      <span className={textClassName}>{children}</span>
    </>
  );

  const wrapperClass = `inline-flex items-center gap-1.5 ${className}`.trim();

  if (htmlFor) {
    return (
      <label htmlFor={htmlFor} className={wrapperClass}>
        {content}
      </label>
    );
  }

  return <span className={wrapperClass}>{content}</span>;
};

export default AttributeLabel;
